import type { BoardColumn, BoardSubproject } from '../services/api/board';

/**
 * MKS-419 — one swimlane row of the merged board: a readable subproject with at
 * least one card on the board (after filters).
 */
export interface SubprojectLane {
  id: number;
  slug: string;
  /** Subproject name; a grandchild reads as its path, e.g. "Child › Grandchild". */
  title: string;
  /** Cards in this lane across all columns. */
  count: number;
}

/** Row-fold key; cannot collide with a milestone id or 'no-milestone'. */
export const subprojectLaneKey = (projectId: number): string => `sp:${projectId}`;

/**
 * Builds the subproject lanes of the merged board, in render order, and drops the
 * lanes without cards: 41 empty presales lanes would bury the board.
 *
 * Order: depth-first, children directly after their parent. Siblings keep the order
 * of `subprojects` (the API sorts by name). A subproject whose parent is the board's
 * project, or is not readable by the viewer, starts at the top level, and a parent
 * cycle cannot loop (leftovers are appended in API order).
 *
 * `columns` should be the non-triage columns AFTER the filters, so the counts and the
 * hidden-when-empty rule follow what the viewer is looking at.
 */
export function buildSubprojectLanes(
  subprojects: readonly BoardSubproject[],
  rootProjectId: number,
  columns: readonly Pick<BoardColumn, 'tasks'>[],
): SubprojectLane[] {
  const counts = new Map<number, number>();
  for (const col of columns) {
    for (const t of col.tasks) counts.set(t.projectId, (counts.get(t.projectId) ?? 0) + 1);
  }

  const known = new Set(subprojects.map((sp) => sp.id));
  const childrenOf = new Map<number, BoardSubproject[]>();
  const topLevel: BoardSubproject[] = [];
  for (const sp of subprojects) {
    const parent = sp.parentId ?? null;
    if (parent === null || parent === rootProjectId || parent === sp.id || !known.has(parent)) {
      topLevel.push(sp);
    } else {
      const siblings = childrenOf.get(parent);
      if (siblings) siblings.push(sp);
      else childrenOf.set(parent, [sp]);
    }
  }

  const lanes: SubprojectLane[] = [];
  const visited = new Set<number>();
  const visit = (sp: BoardSubproject, path: string[]) => {
    if (visited.has(sp.id)) return;
    visited.add(sp.id);
    const label = sp.name?.trim() ? sp.name : sp.slug;
    const trail = [...path, label];
    const count = counts.get(sp.id) ?? 0;
    if (count > 0) {
      lanes.push({ id: sp.id, slug: sp.slug, title: trail.join(' › '), count });
    }
    for (const child of childrenOf.get(sp.id) ?? []) visit(child, trail);
  };
  for (const sp of topLevel) visit(sp, []);
  // A parent cycle leaves nodes unreachable from the top level; keep them visible.
  for (const sp of subprojects) visit(sp, []);
  return lanes;
}
